import React, { useEffect, useState } from 'react';
import { Check, Palette, TextAa, ArrowCounterClockwise } from '@phosphor-icons/react';
import { useTheme, THEMES } from '../hooks/useTheme';

const FONT_KEY = 'dws_font_scale';
const FONT_OPTIONS = [
  { value: 0.9, label: 'Small' },
  { value: 1.0, label: 'Default' },
  { value: 1.1, label: 'Large' },
  { value: 1.2, label: 'Extra Large' },
];

/**
 * Admin/Handler Settings — dedicated Appearance card with bigger theme previews
 * and a font-size slider. Same theme storage as the chat three-dot picker.
 *
 * IMPORTANT: The settings panel itself is rendered inside a font-scale-immune
 * wrapper (fixed 16px root em) so users can always shrink the font back down
 * even after scaling up.
 */
const AppearanceSettings = () => {
  const { theme, setTheme } = useTheme();
  const [fontScale, setFontScale] = useState(() => {
    try { return parseFloat(localStorage.getItem(FONT_KEY) || '1'); } catch { return 1; }
  });

  useEffect(() => {
    document.documentElement.style.setProperty('--dws-font-scale', String(fontScale));
    document.documentElement.style.fontSize = `${fontScale * 100}%`;
    try { localStorage.setItem(FONT_KEY, String(fontScale)); } catch {}
  }, [fontScale]);

  return (
    // fontSize:'16px' + '1rem = 16px' keeps this panel immune to global scaling,
    // so the sliders/buttons remain reachable even at Extra Large.
    <div
      className="bg-white rounded-lg shadow-sm p-6 mt-6"
      style={{ fontSize: '16px' }}
      data-testid="appearance-settings"
    >
      <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-1" style={{ fontSize: '18px' }}>
        <span style={{ fontSize: '20px' }}>🎨</span> Appearance
      </h3>
      <p className="text-gray-500 mb-4" style={{ fontSize: '13px' }}>Personalize how the app looks on this device.</p>

      <div className="mb-6">
        <div className="font-semibold text-gray-600 mb-2 flex items-center gap-1.5" style={{ fontSize: '12px' }}>
          <Palette size={14} weight="fill" className="text-indigo-500" /> Theme
        </div>
        <div className="grid grid-cols-5 gap-2">
          {THEMES.map(t => {
            const isActive = theme === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTheme(t.key)}
                data-testid={`appearance-theme-${t.key}`}
                className={`rounded-lg overflow-hidden border-2 transition ${isActive ? 'border-blue-500 shadow-md' : 'border-transparent hover:border-gray-200'}`}
              >
                <div className="h-14 relative" style={{ background: t.swatch }}>
                  {isActive && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white/95 rounded-full w-6 h-6 flex items-center justify-center">
                        <Check size={14} weight="bold" className="text-blue-600" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="font-semibold text-gray-700 py-1 bg-gray-50 text-center" style={{ fontSize: '10px' }}>{t.label}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold text-gray-600 flex items-center gap-1.5" style={{ fontSize: '12px' }}>
            <TextAa size={14} weight="fill" className="text-orange-500" /> Font Size
          </div>
          <button
            onClick={() => setFontScale(1.0)}
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium"
            style={{ fontSize: '11px' }}
            data-testid="appearance-font-reset"
            title="Reset to Default"
          >
            <ArrowCounterClockwise size={12} weight="bold" /> Reset
          </button>
        </div>
        <input
          type="range"
          min="0.9"
          max="1.2"
          step="0.05"
          value={fontScale}
          onChange={(e) => setFontScale(parseFloat(e.target.value))}
          className="w-full accent-blue-600"
          data-testid="appearance-font-slider"
        />
        <div className="flex justify-between text-gray-500 mt-1 px-1" style={{ fontSize: '10px' }}>
          {FONT_OPTIONS.map(f => (
            <button
              key={f.value}
              onClick={() => setFontScale(f.value)}
              data-testid={`appearance-font-preset-${f.label.toLowerCase().replace(' ', '-')}`}
              className={`hover:text-blue-600 ${Math.abs(fontScale - f.value) < 0.01 ? 'font-bold text-blue-600' : ''}`}
              style={{ fontSize: '11px' }}
            >{f.label}</button>
          ))}
        </div>
        {/* Preview strip */}
        <div className="mt-3 rounded-lg border border-gray-200 p-3 bg-gray-50">
          <div className="uppercase text-gray-500 mb-1" style={{ fontSize: '10px' }}>Preview</div>
          <div className="font-semibold text-gray-800" style={{ fontSize: `${fontScale}em` }}>
            The quick brown fox jumps over the lazy dog.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppearanceSettings;

